import { useDocChat } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChat'
import { useUnifiedDocumentSearch } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useUnifiedDocumentSearch'
import { activeDocChatTurnHasAttachments } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import type { DocChatPendingAttachment } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatImageAttachments'
import type {
  DocChatContextDocument,
  DocumentSearchResult,
  SearchEngine,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import {
  getDocumentSearchEngine,
  isAiDocumentSearchEngine,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchMode'
import type {
  DocumentSearchMode,
  SearchHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'

interface UseEnabledDocumentSearchArgs {
  siteId: string
  tabId?: string
  searchQuery: string
  contentFilter: DocumentsViewerContentFilter
  searchMode: DocumentSearchMode
  linkedMode: boolean
  browseAll: boolean
  docChat?: SearchHistoryItem['searchState']['docChat']
  attachments?: DocChatPendingAttachment[]
  contextDocuments?: DocChatContextDocument[]
}

interface UseEnabledDocumentSearchResult {
  rawDocumentSearchData: DocumentSearchResult | undefined
  streamingSearchData: DocumentSearchResult | undefined
  isLoadingSearch: boolean
  searchEngine: SearchEngine
  searchLatencyMs: number | undefined
  refreshDocumentSearch: () => void
}

function shouldSubmitDocChat(
  docChat: SearchHistoryItem['searchState']['docChat'] | undefined
): boolean {
  return docChat?.isHydratedHistory !== true
}

export function useEnabledDocumentSearch({
  siteId,
  tabId,
  searchQuery,
  contentFilter,
  searchMode,
  linkedMode,
  browseAll,
  docChat,
  attachments,
  contextDocuments,
}: UseEnabledDocumentSearchArgs): UseEnabledDocumentSearchResult {
  const searchEngine = getDocumentSearchEngine({
    searchMode,
    contentFilter,
    isLinkedDocsMode: linkedMode,
    browseAll,
  })
  const shouldUseDocChat = isAiDocumentSearchEngine(searchEngine)
  // Persisted turn metadata counts too, so a reload mid-turn does not disable
  // an attachment-only turn whose bytes now live only in the attachment store.
  const hasAttachments =
    (attachments?.length ?? 0) > 0 ||
    activeDocChatTurnHasAttachments(
      docChat?.siteId === siteId ? docChat.conversation : undefined
    )
  const shouldSearchDocuments =
    contentFilter === 'documents' &&
    !linkedMode &&
    !browseAll &&
    (searchQuery.trim().length > 0 || (shouldUseDocChat && hasAttachments))
  const unifiedDocumentSearch = useUnifiedDocumentSearch({
    enabled: shouldSearchDocuments && !shouldUseDocChat,
    siteId,
    searchQuery,
  })
  const docChatSearch = useDocChat({
    enabled: shouldSearchDocuments && shouldUseDocChat,
    shouldStartStream: shouldSubmitDocChat(docChat),
    siteId,
    tabId,
    searchQuery,
    docChat,
    attachments,
    contextDocuments,
  })
  const activeDocumentSearch = shouldUseDocChat
    ? docChatSearch
    : unifiedDocumentSearch

  return {
    ...activeDocumentSearch,
    searchEngine,
  }
}
